import type { SupplierKind } from "./enums";
import { parseDecimal } from "./quantity";

/**
 * Money is integer minor units plus an ISO 4217 currency (architecture.md › Quantities and money).
 */
export interface Money {
  amountMinor: number;
  currency: string;
}

export const CURRENCIES = [
  "EUR",
  "USD",
  "GBP",
  "CHF",
  "SEK",
  "NOK",
  "DKK",
  "PLN",
  "CZK",
  "CAD",
  "AUD",
  "JPY",
] as const;

export type CurrencyCode = (typeof CURRENCIES)[number];

const digitsCache = new Map<string, number>();

export function currencyDigits(currency: string): number {
  let digits = digitsCache.get(currency);
  if (digits === undefined) {
    digits =
      new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2;
    digitsCache.set(currency, digits);
  }
  return digits;
}

export function toMinor(amount: number, currency: string): number {
  return Math.round(amount * 10 ** currencyDigits(currency));
}

export function fromMinor(amountMinor: number, currency: string): number {
  return amountMinor / 10 ** currencyDigits(currency);
}

export function parseMoney(input: string, currency: string): number | null {
  const amount = parseDecimal(input.replace(/[^\d.,\-\s']/g, ""));
  return amount === null ? null : toMinor(amount, currency);
}

/** Line total of a unit price (minor units) for a quantity (milli-units). */
export function lineTotalMinor(unitPriceMinor: number, qty: number): number {
  return Math.round((unitPriceMinor * qty) / 1000);
}

/** The lowest and highest of several prices in one currency ("12,90 – 15,40 €"). */
export interface PriceRange {
  minMinor: number;
  maxMinor: number;
  currency: string;
}

/** The range of the prices given in `currency`; others are left out. Null when none is left. */
export function priceRange(
  prices: readonly { priceMinor: number | null; currency: string }[],
  currency: string,
): PriceRange | null {
  let range: PriceRange | null = null;
  for (const { priceMinor, currency: own } of prices) {
    if (priceMinor === null || own !== currency) continue;
    range = range
      ? {
          minMinor: Math.min(range.minMinor, priceMinor),
          maxMinor: Math.max(range.maxMinor, priceMinor),
          currency,
        }
      : { minMinor: priceMinor, maxMinor: priceMinor, currency };
  }
  return range;
}

/** A source's price and who sells there. */
export interface SourcePrice {
  priceMinor: number | null;
  currency: string;
  url: string | null;
  supplierKind: SupplierKind | null;
  supplierName: string | null;
}

export interface OnlinePrice {
  priceMinor: number;
  currency: string;
  supplierName: string | null;
}

/**
 * A part's online price: the lowest price in `currency` among its sources at online shops, or with
 * a link and no supplier. Prices at local dealers are left out, since they are what it's compared
 * with. Null when there is none.
 */
export function cheapestOnlinePrice(
  sources: readonly SourcePrice[],
  currency: string,
): OnlinePrice | null {
  let best: OnlinePrice | null = null;
  for (const source of sources) {
    if (source.priceMinor === null || source.currency !== currency) continue;
    const online =
      source.supplierKind === "online" || (source.supplierKind === null && source.url !== null);
    if (!online) continue;
    if (!best || source.priceMinor < best.priceMinor) {
      best = { priceMinor: source.priceMinor, currency, supplierName: source.supplierName };
    }
  }
  return best;
}

/** What a list of parts costs at the cheapest and the dearest prices known. */
export interface CostEstimate extends PriceRange {
  /** Lines counted. */
  priced: number;
  /** Lines without a price in the estimate's currency, which the range leaves out. */
  unpriced: number;
}

/**
 * The cost range of parts lines: quantity (milli-units) times each part's price range. Tools
 * (lines that don't consume stock) are left out, as for work orders.
 */
export function estimateRange(
  lines: readonly { qty: number; consumesStock: boolean; price: PriceRange | null }[],
  currency: string,
): CostEstimate {
  const estimate: CostEstimate = { minMinor: 0, maxMinor: 0, currency, priced: 0, unpriced: 0 };
  for (const line of lines) {
    if (!line.consumesStock || line.qty === 0) continue;
    if (!line.price || line.price.currency !== currency) {
      estimate.unpriced += 1;
      continue;
    }
    estimate.minMinor += lineTotalMinor(line.price.minMinor, line.qty);
    estimate.maxMinor += lineTotalMinor(line.price.maxMinor, line.qty);
    estimate.priced += 1;
  }
  return estimate;
}
